.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;   /* lets a field shrink inside a grid instead of overflowing */
}

.label {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text);
  padding: 0;
}

.required {
  color: var(--danger);
  margin-left: 2px;
}

.hint {
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: var(--leading-normal);
  margin-top: calc(var(--space-1) * -1);
}

.input {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-base);
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  min-height: 36px;
}

.input::placeholder { color: var(--text-subtle); }

.input:hover:not(:disabled):not(:focus) { border-color: var(--text-subtle); }

.input:focus {
  outline: none;
  border-color: var(--indigo);
  box-shadow: 0 0 0 3px rgb(79 63 240 / 15%);
}

.input:disabled {
  background: var(--bg-sunken);
  color: var(--text-muted);
  cursor: not-allowed;
}

/* Error state is border + message, never colour alone — roughly 1 in 12 men
   has a red/green colour vision deficiency. */
.inputError {
  border-color: var(--danger);
}
.inputError:focus {
  border-color: var(--danger);
  box-shadow: 0 0 0 3px rgb(220 38 38 / 15%);
}

.error {
  font-size: var(--text-xs);
  color: var(--danger);
  display: flex;
  align-items: center;
  gap: var(--space-1);
}
.error::before {
  content: '⚠';
  font-size: 1em;
}

.select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%2364748b' d='M4.5 6.5 8 10l3.5-3.5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--space-3) center;
  background-size: 16px;
  padding-right: var(--space-6);
  cursor: pointer;
}

.textarea {
  min-height: 72px;
  resize: vertical;
  line-height: var(--leading-normal);
}

/* --------------------------------------------------------------- prefixed */
.inputWithPrefix {
  position: relative;
  display: flex;
  align-items: center;
}

.prefix {
  position: absolute;
  left: var(--space-3);
  color: var(--text-muted);
  font-size: var(--text-base);
  pointer-events: none;
}

.inputWithPrefix .input {
  padding-left: var(--space-6);
}

/* --------------------------------------------------------------- checkbox */
.checkboxField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.checkboxRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--indigo);
  cursor: pointer;
  flex-shrink: 0;
}

.checkboxLabel {
  font-size: var(--text-base);
  color: var(--text);
  cursor: pointer;
  user-select: none;
}

.checkboxHint {
  font-size: var(--text-xs);
  color: var(--text-muted);
  /* Aligned under the label text, past the checkbox. */
  padding-left: calc(16px + var(--space-2));
}

/* ----------------------------------------------------------- chip group */
.checkboxGroup {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.checkboxChip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
  background: var(--bg-elevated);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-full);
  cursor: pointer;
  user-select: none;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.checkboxChip:hover {
  background: var(--bg-hover);
  border-color: var(--text-subtle);
}

/* `:has()` lets the chip reflect its own checkbox without extra JS state. */
.checkboxChip:has(input:checked) {
  background: var(--indigo-soft);
  border-color: var(--indigo);
  color: var(--indigo);
  font-weight: var(--weight-medium);
}

.emptyOptions {
  font-size: var(--text-sm);
  color: var(--text-muted);
  font-style: italic;
  padding: var(--space-2) 0;
}

/* *** NO SPINNER BUTTONS ON A NUMBER FIELD ***
   The little up/down arrows invite a click that changes a price by one, and
   they sit exactly where a cursor lands when reaching for the text. Combined
   with the wheel and arrow-key guards in Field.tsx, the value now changes only
   when it is typed.

   Both rules are needed: WebKit/Blink draw the buttons as a pseudo-element,
   Firefox as a property of the input itself. */
.noSpinner::-webkit-outer-spin-button,
.noSpinner::-webkit-inner-spin-button {
  appearance: none;
  margin: 0;
}

.noSpinner {
  -moz-appearance: textfield;   /* Firefox */
  appearance: textfield;
}
